import {
  BodyText,
  Button,
  Heading,
  Icon,
  IconSet,
} from '@postscript/components';

import { useGetCurrentPlan } from 'components/billing/context/useBilling';
import { useUser } from 'controllers/contexts/user';

import { PricingDisclosure } from 'components/billing/admin/promotions/BundledAI/shared/PricingDisclosure';

import {
  BUNDLED_AI_FEATURE_KEY,
  PROGRESSIVE_ONBOARDING_SOURCE,
} from 'providers/ProgressiveOnboarding/constants/constants';
import { logButtonClickEvent, PRODUCT_AREAS } from 'utils/events';

import classes from 'components/billing/admin/promotions/BundledAI/onboarding/SplashScreen.module.css';

const SelectionCard = ({
  icon,
  title,
  body,
}: {
  icon: keyof typeof IconSet;
  title: string;
  body: JSX.Element;
}) => {
  return (
    <div className={classes.selectionCard}>
      <Icon component={IconSet[icon]} color="var(--blue-core)" />

      <div className={classes.text}>
        <Heading size="xx-small">{title}</Heading>

        {body}
      </div>
    </div>
  );
};

export const Splashscreen = ({ dismiss }: { dismiss: () => void }) => {
  const { user } = useUser();
  const { data: marketingPlan } = useGetCurrentPlan();

  const onClickLearnMore = (event: React.MouseEvent<HTMLButtonElement>) => {
    window.open(
      'https://postscript.io/chat-with-sales?utm_source=postscript&utm_medium=in_app&utm_campaign=postscript_ai_plan',
      '_blank',
    );

    logButtonClickEvent(event, {
      productArea: PRODUCT_AREAS.BILLING,
      source: PROGRESSIVE_ONBOARDING_SOURCE,
      workflow: BUNDLED_AI_FEATURE_KEY,
      shop_id: user.shop_id,
    });
  };

  return (
    <div className={classes.splashScreen}>
      <div className={classes.header}>
        <Heading size="medium" className={classes.title}>
          Upgrade SMS Marketing with revenue-driving AI
        </Heading>
      </div>

      <div className={classes.body}>
        <SelectionCard
          icon="SettingsSliders"
          title="Brand Center"
          body={
            <BodyText size="small">
              <ul>
                <li>Train the AI models across all Postscript AI products.</li>
                <li>
                  Manage your brand voice preferences, knowledge base, offers,
                  product catalogue and more.
                </li>
              </ul>
            </BodyText>
          }
        />

        <SelectionCard
          icon="Sparkles"
          title="Shopper"
          body={
            <BodyText size="small">
              <ul className={classes.list}>
                <li>Your always-on AI shopping assistant for SMS.</li>
                <li>
                  Have 1:1 customer conversations over SMS that convert,
                  delight, and inspire - all in your brand voice.
                </li>
              </ul>
            </BodyText>
          }
        />

        <SelectionCard
          icon="InfinityTesting"
          title="Infinity Testing"
          body={
            <BodyText size="small">
              With Infinity Testing for Automations and Campaigns, test hundreds
              of on-brand variants in a fraction of the time—and drive
              incredible results.
            </BodyText>
          }
        />

        <div className={classes.pricing}>
          <PricingDisclosure />
        </div>
      </div>

      <div className={classes.footer}>
        <Button variant="primary" onClick={dismiss}>
          Get Started
        </Button>

        {(marketingPlan?.package?.sku === 'PROFESSIONAL' ||
          marketingPlan?.package?.sku === 'ENTERPRISE') && (
          <Button variant="plain" onClick={onClickLearnMore}>
            Talk to Sales
          </Button>
        )}
      </div>
    </div>
  );
};
